import React from 'react';
import clsx from 'clsx';
import { Select } from '@pickatale/ui-kit';
import { useTooltip, TooltipPosition } from '@/context/Ui';

type TitleElementName = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

interface ManagePanelTitleAction {
  label?: React.ReactNode;
  onClick: () => void;
  options?: Array<{
    label: string;
    value: string | undefined;
  }>;
  selectedOption?: string;
  placeHolder?: string;
  optionValue?: string | undefined;
  setValue?: (value: string | undefined) => void;
}

interface ManagePanelTitleProps {
  className?: string;
  innerClassName?: string;
  onClick?: () => void;
  TitleElement?: TitleElementName;
  withoutUnderline?: boolean;
  actions?: Array<ManagePanelTitleAction>;
  tooltipText?: string;
  tooltipPosition?: TooltipPosition;
}

export const ManagePanelTitle = ({
  children,
  className,
  innerClassName,
  onClick,
  TitleElement = 'h2',
  withoutUnderline = false,
  actions = [],
  tooltipText,
  tooltipPosition = 'top',
}: React.PropsWithChildren<ManagePanelTitleProps>) => {
  const { tooltipElementRef } = useTooltip(tooltipText, {
    position: tooltipPosition,
  });

  return (
    <TitleElement
      className={clsx(
        'font-bold text-pt-shark dark:text-white',
        !withoutUnderline &&
          'pb-2 border-b-[1px] border-pt-silver border-solid',
        actions.length &&
          'flex flex-col md:flex-row justify-between items-center',
        className,
      )}
    >
      {onClick ? (
        <button
          ref={
            tooltipText
              ? (tooltipElementRef as React.RefObject<HTMLButtonElement | null>)
              : undefined
          }
          className={clsx('flex gap-1', innerClassName)}
          onClick={onClick}
        >
          {children}
        </button>
      ) : (
        <div
          ref={
            tooltipText
              ? (tooltipElementRef as React.RefObject<HTMLDivElement | null>)
              : undefined
          }
          className={clsx('flex gap-1', innerClassName)}
        >
          {children}
        </div>
      )}
      {actions.length > 0 && (
        <div className="flex gap-1">
          {actions.map((action, index) =>
            action.options?.length ? (
              <div key={index} className="w-72">
                <Select
                  name="filterLetterSet"
                  label={action.label as string}
                  options={action.options.map(({ label, value }) => ({
                    label,
                    value: value?.toString() || '-1',
                  }))}
                  placeholder={action.placeHolder || 'Select option'}
                  displayRadioDots={false}
                  hideLabel
                  selectedValue={action.optionValue}
                  onValueChange={(value) =>
                    action.setValue?.(value === '-1' ? undefined : value)
                  }
                />
              </div>
            ) : (
              <button key={index}>{action.label}</button>
            ),
          )}
        </div>
      )}
    </TitleElement>
  );
};

interface ManagePanelProps {
  className?: string;
  bgColorScheme?: 'green' | 'white' | 'yellow';
  bannerCorners?: boolean;
  notFlex?: boolean;
  yOrientation?: boolean;
  spacing?: 'sm' | 'md';
  title?: string;
  onClickTitle?: () => void;
  titleWithoutUnderline?: boolean;
  titleClassName?: string;
  titleInnerClassName?: string;
  titleAccessory?: React.ReactNode;
  titleTooltipText?: string;
  titleTooltipPosition?: TooltipPosition;
  actions?: Array<ManagePanelTitleAction>;
  TitleElement?: TitleElementName;
}

export const ManagePanel = ({
  children,
  className,
  bgColorScheme = 'green',
  bannerCorners = false,
  notFlex = false,
  yOrientation = false,
  spacing = 'md',
  title,
  onClickTitle,
  titleWithoutUnderline = false,
  titleClassName,
  titleInnerClassName,
  titleAccessory,
  titleTooltipText,
  titleTooltipPosition = 'top',
  actions = [],
  TitleElement = 'h2',
}: React.PropsWithChildren<ManagePanelProps>) => (
  <div
    className={clsx(
      !notFlex && 'flex',
      yOrientation && !notFlex && 'flex-col',
      bannerCorners
        ? 'rounded-bl-3xl rounded-tr-3xl'
        : bgColorScheme === 'white'
          ? 'rounded'
          : 'rounded-xl',
      spacing === 'sm' && 'p-3 gap-3',
      spacing === 'md' && 'p-6 gap-6 ',
      bgColorScheme === 'green' && 'bg-pt-secondary dark:bg-pt-dove-gray ',
      bgColorScheme === 'white' && 'bg-white dark:bg-pt-dove-gray shadow-panel',
      bgColorScheme === 'yellow' && 'bg-pt-lightening-yellow  ',
      className,
    )}
  >
    {title && (
      <ManagePanelTitle
        className={titleClassName}
        innerClassName={titleInnerClassName}
        onClick={onClickTitle}
        TitleElement={TitleElement}
        withoutUnderline={titleWithoutUnderline}
        actions={actions}
        tooltipText={titleTooltipText}
        tooltipPosition={titleTooltipPosition}
      >
        {titleAccessory}
        {` `}
        {title}
      </ManagePanelTitle>
    )}

    {children}
  </div>
);
